import React, { useCallback, useMemo, useState } from 'react';
import {
    FlatList,
    Platform,
    StyleSheet,
    Text,
    View,
} from 'react-native';
import { getNumberOfSteps, getTopInset } from '../../../../utils/shared';
import {
    mbApplyTransparency,
    mbShadow,
    mbTextStyles,
} from '@mightybyte/rnw.utils.style-utils';
import { FONT_STYLES, textStyles } from '../../../../constants/textStyles';
import Feather from 'react-native-vector-icons/Feather';
import { Spacer } from '../../../helperComponents/Spacer';
import { ShadowView } from '../../../helperComponents/ShadowView';
import CoinsIcon from '../../../../resources/svgComponents/CoinsIcon';
import Stepper from '../../../helperComponents/Stepper';
import { useSignedInContext } from '../../../../context/SignedInContext';
import {
    AgeGroup,
    GroupBonuses,
    QuestionToPlay,
    UserIdentity,
} from '@sapphicsavvy/business';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { Answer } from '../../../../navigations/RootNavigator';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';
import RowSliderCard from '../components/RowSliderCard';
import { StepOverlay } from '../components/StepOverlay';
import { Overlay } from '../../../helperComponents/Overlay';
import { AnimatedFastImage, seconds } from '../../../../utils/animations';

type GroupKey = keyof typeof UserIdentity | keyof typeof AgeGroup;

const GroupState: Record<GroupKey, number> = {
    lateTeens: 0,
    '20s': 0,
    '30s': 0,
    '40s': 0,
    '50s+': 0,
    lesbian: 0,
    bisexual: 0,
    queer: 0,
};

type OtherGroupBonusStepProp = {
    onNext?: (answer: Partial<Answer>) => void;
    onBack?: () => void;
    question: QuestionToPlay;
    isSubmitting?: boolean;
    points?: number;
};

export default function OtherGroupBonusStep({
    question,
    onNext,
    onBack,
    isSubmitting,
    points,
}: OtherGroupBonusStepProp) {
    const { currentUserData } = useSignedInContext();
    const [groupState, setGroupState] = useState(GroupState);

    const groups = useMemo<GroupKey[]>(() => {
        if (
            question.identityGroupBonus?.bonusType === GroupBonuses.identity &&
            currentUserData?.identity
        ) {
            return Object.values(UserIdentity).filter(
                (identity) => identity !== currentUserData?.identity,
            );
        } else if (
            question.identityGroupBonus?.bonusType === GroupBonuses.ageGroup &&
            currentUserData?.ageGroup
        ) {
            return Object.values(AgeGroup).filter(
                (age) => age !== currentUserData?.ageGroup,
            );
        }
        return [];
    }, [
        currentUserData?.ageGroup,
        currentUserData?.identity,
        question.identityGroupBonus?.bonusType,
    ]);

    const itemSeparator = useCallback(() => <Spacer height={15} />, []);

    const imageUri = mbGetMediumImage(question.imageUrl, { asUri: true });

    const min = question.answerOptions.min || 0;
    const max = question.answerOptions.max || 100;
    const increment = question.answerOptions.increment || 1;
    const strictMax = question.answerOptions.strictMax;

    const answer = useMemo(() => {
        if (question.identityGroupBonus?.bonusType === GroupBonuses.identity) {
            return {
                identityGroupBonus: {
                    bisexual: groupState.bisexual,
                    lesbian: groupState.lesbian,
                    queer: groupState.queer,
                },
            };
        } else {
            return {
                ageGroupBonus: {
                    lateTeens: groupState.lateTeens,
                    '20s': groupState['20s'],
                    '30s': groupState['30s'],
                    '40s': groupState['40s'],
                    '50s+': groupState['50s+'],
                },
            };
        }
    }, [groupState, question.identityGroupBonus?.bonusType]);

    return (
        <View style={styles.container}>
            <StepOverlay step="bonus" />
            <Overlay delay={seconds(2)} />
            <View style={styles.header}>
                <Text style={styles.headerTitleText}>OTHER GROUP BONUS</Text>
                <Spacer />
                <ShadowView style={styles.coins}>
                    <CoinsIcon size={27} />
                    <Spacer width={6} />
                    <Text style={styles.pointsText}>{points}</Text>
                </ShadowView>
            </View>
            <View style={styles.topSection}>
                <Stepper
                    style={styles.topSectionStepper}
                    step={4}
                    max={getNumberOfSteps(question)}
                />
                <View style={styles.topSectionInfo}>
                    <Text style={styles.topSectionTitleText}>
                        {question.identityGroupBonus?.question}
                    </Text>
                    <AnimatedFastImage
                        delay={seconds(2)}
                        fromScale={0.8}
                        toScale={1}
                        resizeMode={Platform.OS === 'ios' ? 'contain' : 'cover'}
                        style={styles.topSectionImage}
                        source={imageUri}
                    />
                </View>
            </View>
            <FlatList
                style={styles.bottomSection}
                data={groups}
                contentContainerStyle={styles.bottomSectionContent}
                renderItem={({ item }) => (
                    <RowSliderCard
                        title={item}
                        value={groupState[item]}
                        onChange={(value) =>
                            setGroupState((prev) => ({
                                ...prev,
                                [item]: value,
                            }))
                        }
                        steps={increment}
                        min={min}
                        max={max}
                        answerType={question.answerType}
                        strictMax={strictMax}
                    />
                )}
                ItemSeparatorComponent={itemSeparator}
            />
            <View style={styles.footer}>
                <MB_Button
                    style={styles.backBtn}
                    leftElement={
                        <Feather
                            name="chevron-left"
                            color={styles.backBtnText.color}
                            size={20}
                        />
                    }
                    title="BACK"
                    textStyle={styles.backBtnText}
                    onPress={onBack}
                    disabled={isSubmitting}
                />
                <MB_Button
                    style={styles.nextBtn}
                    rightElement={
                        <Feather
                            name="chevron-right"
                            color={styles.nextBtnText.color}
                            size={20}
                        />
                    }
                    title="NEXT"
                    textStyle={styles.nextBtnText}
                    onPress={() => onNext?.({ ...answer })}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#EFEFF3',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 10,
        marginTop: getTopInset(),
    },
    coins: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        width: 111,
        height: 40,
    },
    pointsText: mbTextStyles([
        textStyles.largerText,
        {
            fontSize: 25,
            fontFamily: FONT_STYLES.Tabasco_400,
            fontWeight: '400',
            color: '#F35E2E',
        },
    ]),
    headerTitleText: mbTextStyles([
        textStyles.normalText,
        {
            fontFamily: FONT_STYLES.Tabasco_400,
            textAlign: 'left',
            fontWeight: '400',
            fontSize: 22,
            color: mbApplyTransparency('#FCAF41', 0.95),
        },
    ]),
    topSection: {
        flex: 1,
        backgroundColor: mbApplyTransparency('#FCAF41', 0.95),
    },
    topSectionInfo: {
        paddingVertical: 20,
        paddingHorizontal: 20,
        flex: 1,
    },
    topSectionImage: Platform.select({
        default: {
            width: '100%',
            flex: 1,
            borderRadius: 12,
        },
        android: {
            aspectRatio: 2,
            alignSelf: 'center',
            flex: 1,
            borderRadius: 12,
        },
    }),
    topSectionStepper: {
        margin: 2,
    },
    topSectionTitleText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 21,
            fontWeight: '600',
            marginBottom: 21,
        },
    ]),
    topSectionTitleBoldText: mbTextStyles([
        textStyles.largeText,
        {
            fontSize: 55,
            fontWeight: '700',
            marginVertical: 14,
        },
    ]),
    bottomSection: {
        flex: 1,
    },
    bottomSectionContent: {
        padding: 15,
    },
    footer: {
        flexDirection: 'row',
        height: 60,
        ...mbShadow({
            offsetWidth: 5,
            offsetHeight: 5,
            radius: 10,
            elevation: 10,
            color: '#AEAEC0',
            opacity: 0.5,
        }),
    },
    backBtn: {
        backgroundColor: '#F0F0F3',
        flex: 1,
        height: '100%',
        paddingBottom: 10,
        borderRadius: 0,
    },
    backBtnText: mbTextStyles([
        textStyles.normalText,
        {
            fontSize: 18,
            fontWeight: '800',
            color: '#7E8F9F',
        },
    ]),
    nextBtn: {
        backgroundColor: mbApplyTransparency('#FCAF41', 0.95),
        flex: 1,
        height: '100%',
        paddingBottom: 10,
        borderRadius: 0,
    },
    nextBtnText: mbTextStyles([
        textStyles.normalText,
        {
            fontSize: 18,
            fontWeight: '800',
            color: '#FFFFFF',
        },
    ]),
});
